import { trpc, RouterOutputs } from '@org/clients/school';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Skeleton,
  Switch,
  Table,
  unwrapTrpcData,
} from '@org/ui';
import { createColumnHelper } from '@tanstack/react-table';
import { endOfDay, format } from 'date-fns';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ProductSelect } from '../product/product-select';
import { PendingRequestsPanel } from '../product/pending-requests-panel';
import { BaseAccessSection } from './base-access-section';
import { getMutationErrorMessage } from '../shared/multi-step-utils';

type AccessRow = RouterOutputs['journeyAccess']['listForUser'][number];

type JourneyAccessDialogProps = {
  userId: string;
  userLabel?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

// Only rows the admin created (or that came from a purchase) have an access
// window the admin may change; teacher and path rows are managed elsewhere.
const isEditableSource = (source: AccessRow['source']) =>
  source === 'purchase' || source === 'admin';

const toDate = (value: unknown): Date | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value as string);
  return Number.isNaN(date.getTime()) ? null : date;
};

type WindowEditor = {
  journeyId: string;
  endsAt: Date | undefined;
  never: boolean;
};

export function JourneyAccessDialog({ userId, userLabel, open, onOpenChange }: JourneyAccessDialogProps) {
  const { t } = useTranslation('admin');
  const utils = trpc.useUtils();

  const [grantProductId, setGrantProductId] = useState<string | undefined>(undefined);
  const [grantUseDefault, setGrantUseDefault] = useState(true);
  const [grantNever, setGrantNever] = useState(false);
  const [grantEndsAt, setGrantEndsAt] = useState<Date | undefined>(undefined);
  const [grantNote, setGrantNote] = useState('');
  const [editor, setEditor] = useState<WindowEditor | null>(null);

  const { data, isLoading } = trpc.journeyAccess.listForUser.useQuery(
    { userId },
    { enabled: open && !!userId },
  );
  const rows = unwrapTrpcData<AccessRow[]>(data as AccessRow[]) ?? [];

  const invalidate = async () => {
    await utils.journeyAccess.listForUser.invalidate({ userId });
    await utils.journeys.getUserJourneys.invalidate({ userId, activeOnly: false });
    await utils.progress.getActiveJourney.invalidate({ userId });
  };

  const grantMutation = trpc.journeyAccess.grantProduct.useMutation({
    onSuccess: async () => {
      toast.success(t('journeyAccess.dialog.grant.toasts.success'));
      setGrantProductId(undefined);
      setGrantUseDefault(true);
      setGrantNever(false);
      setGrantEndsAt(undefined);
      setGrantNote('');
      await invalidate();
    },
    onError: (error: unknown) =>
      toast.error(t('journeyAccess.dialog.grant.toasts.error', { message: getMutationErrorMessage(error) })),
  });

  const updateWindowMutation = trpc.journeyAccess.updateAccessWindow.useMutation({
    onSuccess: async () => {
      toast.success(t('journeyAccess.dialog.toasts.updateSuccess'));
      setEditor(null);
      await invalidate();
    },
    onError: (error: unknown) =>
      toast.error(t('journeyAccess.dialog.toasts.updateError', { message: getMutationErrorMessage(error) })),
  });

  const revokeMutation = trpc.journeyAccess.revokeAccess.useMutation({
    onSuccess: async () => {
      toast.success(t('journeyAccess.dialog.toasts.revokeSuccess'));
      await invalidate();
    },
    onError: (error: unknown) =>
      toast.error(t('journeyAccess.dialog.toasts.revokeError', { message: getMutationErrorMessage(error) })),
  });

  const handleGrant = () => {
    if (!grantProductId) return;
    // undefined = use the product's default duration; null = never expires.
    const endsAt = grantUseDefault
      ? undefined
      : grantNever
        ? null
        : grantEndsAt
          ? endOfDay(grantEndsAt)
          : undefined;
    grantMutation.mutate({
      userId,
      productId: grantProductId,
      endsAt,
      note: grantNote.trim() ? grantNote.trim() : undefined,
    });
  };

  const handleSaveWindow = () => {
    if (!editor) return;
    if (!editor.never && !editor.endsAt) {
      toast.error(t('journeyAccess.dialog.toasts.pickDate'));
      return;
    }
    updateWindowMutation.mutate({
      userId,
      journeyId: editor.journeyId,
      endsAt: editor.never ? null : endOfDay(editor.endsAt as Date),
    });
  };

  const [pendingRevoke, setPendingRevoke] = useState<AccessRow | null>(null);

  const handleRevoke = (row: AccessRow) => {
    setPendingRevoke(row);
  };

  const confirmRevoke = () => {
    if (!pendingRevoke) return;
    revokeMutation.mutate({ userId, journeyId: pendingRevoke.journeyId });
    setPendingRevoke(null);
  };

  const formatDate = (value: unknown) => {
    const date = toDate(value);
    return date ? format(date, 'PP') : '';
  };

  const sourceLabel = (source: AccessRow['source']) => {
    switch (source) {
      case 'teacher':
        return t('journeyAccess.dialog.source.teacher');
      case 'path':
        return t('journeyAccess.dialog.source.path');
      case 'purchase':
        return t('journeyAccess.dialog.source.purchase');
      case 'admin':
        return t('journeyAccess.dialog.source.admin');
      default:
        return String(source ?? '');
    }
  };

  const statusLabel = (status: AccessRow['status']) =>
    t(`journeyAccess.dialog.status.${status}`, { defaultValue: String(status ?? '') });

  const columnHelper = createColumnHelper<AccessRow>();
  const columns = [
    columnHelper.accessor((row) => row.journeyName ?? '', {
      id: 'journey',
      header: t('journeyAccess.dialog.columns.journey'),
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex flex-col">
            <span className="flex items-center gap-2">
              <span className="font-medium">{info.getValue() || t('journeys.dialog.unnamedJourney')}</span>
              {row.active && (
                <Badge className="bg-emerald-600 text-white hover:bg-emerald-600">
                  {t('journeyAccess.dialog.activeBadge')}
                </Badge>
              )}
            </span>
            {row.levelName && (
              <span className="text-xs text-muted-foreground">{row.levelName}</span>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => sourceLabel(row.source), {
      id: 'source',
      header: t('journeyAccess.dialog.columns.source'),
      cell: (info) => <Badge variant="outline">{info.getValue()}</Badge>,
    }),
    columnHelper.accessor((row) => statusLabel(row.status), {
      id: 'status',
      header: t('journeyAccess.dialog.columns.status'),
    }),
    columnHelper.accessor((row) => formatDate(row.startsAt), {
      id: 'start',
      header: t('journeyAccess.dialog.columns.start'),
    }),
    columnHelper.accessor((row) => (row.endsAt ? formatDate(row.endsAt) : t('journeyAccess.dialog.never')), {
      id: 'end',
      header: t('journeyAccess.dialog.columns.end'),
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex items-center gap-2">
            <span>{info.getValue()}</span>
            {row.expired && (
              <Badge variant="destructive">{t('journeyAccess.dialog.expiredBadge')}</Badge>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.productName ?? '', {
      id: 'product',
      header: t('journeyAccess.dialog.columns.product'),
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex flex-col">
            <span>{info.getValue()}</span>
            {row.purchasedAt && (
              <span className="text-xs text-muted-foreground">
                {formatDate(row.purchasedAt)}
                {row.grantedByName ? ` · ${row.grantedByName}` : ''}
              </span>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.note ?? '', {
      id: 'note',
      header: t('journeyAccess.dialog.columns.note'),
    }),
    columnHelper.display({
      id: 'actions',
      header: t('journeyAccess.dialog.columns.actions'),
      cell: (info) => {
        const row = info.row.original;
        if (!isEditableSource(row.source)) {
          return <span className="text-xs italic text-muted-foreground">{t('journeyAccess.dialog.managedElsewhere')}</span>;
        }
        const current = editor && editor.journeyId === row.journeyId ? editor : null;
        if (current) {
          return (
            <div className="flex flex-col gap-2 min-w-[220px]">
              <DatePicker
                value={current.endsAt}
                onChange={(date) => setEditor({ ...current, endsAt: date })}
                placeholder={t('journeyAccess.dialog.actions.pickEndDate')}
                disabled={current.never}
              />
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <Switch
                  checked={current.never}
                  onCheckedChange={(checked) => setEditor({ ...current, never: checked })}
                />
                {t('journeyAccess.dialog.neverExpires')}
              </label>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleSaveWindow}
                  isLoading={updateWindowMutation.isPending}
                  loadingText={t('common.status.saving')}
                >
                  {t('journeyAccess.dialog.actions.save')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditor(null)} disabled={updateWindowMutation.isPending}>
                  {t('journeyAccess.dialog.actions.cancel')}
                </Button>
              </div>
            </div>
          );
        }
        return (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                setEditor({
                  journeyId: row.journeyId,
                  endsAt: toDate(row.endsAt) ?? undefined,
                  never: !row.endsAt,
                })
              }
            >
              {t('journeyAccess.dialog.actions.changeEnd')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-muted-foreground hover:text-red-600"
              disabled={revokeMutation.isPending || row.expired}
              onClick={() => handleRevoke(row)}
            >
              {t('journeyAccess.dialog.actions.revoke')}
            </Button>
          </div>
        );
      },
    }),
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('journeyAccess.dialog.title', { name: userLabel ?? '' })}</DialogTitle>
          <DialogDescription>{t('journeyAccess.dialog.description')}</DialogDescription>
        </DialogHeader>

        {open && <BaseAccessSection userId={userId} />}

        {open && (
          <PendingRequestsPanel
            userId={userId}
            title={t('pendingRequests.studentBlockTitle')}
            hideStudent
            hideWhenEmpty
          />
        )}

        <Card className="mt-2 shadow-sm">
          <CardHeader className="bg-muted/50 rounded-t-md border-b pb-3 pt-3">
            <CardTitle className="text-base font-medium">{t('journeyAccess.dialog.grant.title')}</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="flex flex-col gap-2 md:col-span-2">
                <Label>{t('journeyAccess.dialog.grant.product')}</Label>
                <ProductSelect value={grantProductId} onChange={setGrantProductId} disabled={grantMutation.isPending} />
              </div>
              <div className="flex flex-col gap-2">
                <Label>{t('journeyAccess.dialog.grant.endDate')}</Label>
                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Switch checked={grantUseDefault} onCheckedChange={setGrantUseDefault} />
                  {t('journeyAccess.dialog.grant.useDefault')}
                </label>
                {!grantUseDefault && (
                  <>
                    <DatePicker
                      value={grantEndsAt}
                      onChange={setGrantEndsAt}
                      placeholder={t('journeyAccess.dialog.actions.pickEndDate')}
                      disabled={grantNever}
                    />
                    <label className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Switch checked={grantNever} onCheckedChange={setGrantNever} />
                      {t('journeyAccess.dialog.neverExpires')}
                    </label>
                  </>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="journey-access-grant-note">{t('journeyAccess.dialog.grant.note')}</Label>
                <Input
                  id="journey-access-grant-note"
                  value={grantNote}
                  onChange={(event) => setGrantNote(event.target.value)}
                  placeholder={t('journeyAccess.dialog.grant.notePlaceholder')}
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button
                variant="outline"
                onClick={handleGrant}
                disabled={!grantProductId || (!grantUseDefault && !grantNever && !grantEndsAt)}
                isLoading={grantMutation.isPending}
                loadingText={t('common.status.saving')}
              >
                {t('journeyAccess.dialog.grant.submit')}
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="mt-6">
          <h3 className="font-medium mb-3 text-foreground border-b pb-2">
            {t('journeyAccess.dialog.listTitle', { count: rows.length })}
          </h3>
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">{t('journeyAccess.dialog.empty')}</p>
          ) : (
            <Table
              tableData={rows}
              columns={columns}
              getRowId={(row: AccessRow) => row.journeyId}
            />
          )}
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('journeyAccess.dialog.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
      <ConfirmDialog
        open={pendingRevoke !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRevoke(null);
        }}
        description={t('journeyAccess.dialog.actions.revokeConfirm', { name: pendingRevoke?.journeyName ?? '' })}
        confirmLabel={t('journeyAccess.dialog.actions.revoke')}
        destructive
        onConfirm={confirmRevoke}
      />
    </Dialog>
  );
}
